import {
  Box,
  Typography,
  Button,
  Container,
  Stack,
  alpha,
} from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useState, useEffect, useMemo } from "react";
import { Link as ScrollLink } from "react-scroll";
import { TypeAnimation } from "react-type-animation";

function HeroSection() {
  const { t, i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [typeAnimationError, setTypeAnimationError] = useState(false);
  const { scrollY } = useScroll();

  const sequence = useMemo(() => {
    return [
      t("hero.badges.0") || "Transform Your Business Today",
      2000,
      t("hero.badges.1") || "Communicate With Your Team",
      2000,
      t("hero.badges.2") || "Automate Your Workflow",
      2000,
      t("hero.badges.3") || "Empower Your Employees",
      2000,
      t("hero.badges.4") || "Achieve Success Together",
      2000,
    ];
  }, [i18n.language, t]);

  // Fallback static text (jika TypeAnimation error)
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
  const staticBadges = useMemo(() => {
    return [
      t("hero.badges.0") || "Transform Your Business Today",
      t("hero.badges.1") || "Communicate With Your Team",
      t("hero.badges.2") || "Automate Your Workflow",
      t("hero.badges.3") || "Empower Your Employees",
      t("hero.badges.4") || "Achieve Success Together",
    ];
  }, [t]);

  // Auto-rotate fallback text jika TypeAnimation gagal
  useEffect(() => {
    if (typeAnimationError) {
      const interval = setInterval(() => {
        setCurrentBadgeIndex((prev) => (prev + 1) % staticBadges.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [typeAnimationError, staticBadges.length]);

  // Dinamis tinggi hero
  const [heroHeight, setHeroHeight] = useState(1000);

  useEffect(() => {
    const updateHeight = () => {
      setHeroHeight(window.innerHeight);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Efek scroll
  const opacity = useTransform(
    scrollY,
    [0, heroHeight * 0.5, heroHeight * 1.0],
    [1, 1, 0]
  );
  const scale = useTransform(scrollY, [0, heroHeight], [1, 0.92]);
  const y = useTransform(scrollY, [0, heroHeight], [0, 140]);

  // Variants animasi masuk
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  return (
    <Box
      id="home"
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        pb: { xs: "100px", md: "150px" },
        background: "linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)",
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0 }}
      >
        <motion.div
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: "10%",
            right: "10%",
            width: 500,
            height: 500,
            background:
              "radial-gradient(circle, rgba(66,133,244,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            filter: "blur(60px)",
          }}
        />
        <motion.div
          animate={{ scale: [1, 1.3, 1], rotate: [0, -90, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            bottom: "5%",
            left: "5%",
            width: 600,
            height: 600,
            background:
              "radial-gradient(circle, rgba(234,67,53,0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            filter: "blur(80px)",
          }}
        />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.random() * 20 - 10, 0],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
            style={{
              position: "absolute",
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 8 + 4}px`,
              height: `${Math.random() * 8 + 4}px`,
              borderRadius: "50%",
              background: `rgba(66,133,244,${Math.random() * 0.5 + 0.2})`,
            }}
          />
        ))}
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          style={{ y, scale, opacity }}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Stack
            spacing={4}
            alignItems="center"
            sx={{
              textAlign: "center",
              px: { xs: 2, sm: 4 },
              py: { xs: 4, md: 6 },
            }}
          >
            {/* Badge with TypeAnimation + Fallback */}
            <motion.div variants={itemVariants}
             style={{
   paddingTop: window.innerWidth < 768 ? "100px" : "120px",
   zIndex: 10,
 }}
>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 3,
                    py: 1,
                    borderRadius: "50px",
                    background:
                      "linear-gradient(135deg, rgba(66,133,244,0.1) 0%, rgba(234,67,53,0.1) 100%)",
                    border: "1.5px solid",
                    borderColor: alpha("#4285F4", 0.3),
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)", // Safari support
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #4285F4 0%, #EA4335 100%)",
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.5 },
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      letterSpacing: "0.05em",
                      // Fallback solid color jika gradient text tidak support
                      color: "#4285F4",
                      background:
                        "linear-gradient(135deg, #4285F4 0%, #EA4335 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      // Fallback untuk browser tanpa support
                      "@supports not (background-clip: text)": {
                        background: "none",
                        color: "#4285F4",
                      },
                      minHeight: "1.2em",
                      display: "inline-block",
                    }}
                  >
                    {!typeAnimationError ? (
                      <TypeAnimationWrapper
                        sequence={sequence}
                        language={i18n.language}
                        onError={() => setTypeAnimationError(true)}
                      />
                    ) : (
                      <motion.span
                        key={currentBadgeIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.5 }}
                      >
                        {staticBadges[currentBadgeIndex]}
                      </motion.span>
                    )}
                  </Typography>
                </Box>
              </motion.div>
            </motion.div>

            {/* Title */}
            <motion.div variants={itemVariants}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: {
                    xs: "2rem",
                    sm: "3rem",
                    md: "3.5rem",
                    lg: "4.2rem",
                  },
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  color: "#2C3E50", // Fallback solid color
                  background:
                    "linear-gradient(135deg, #2C3E50 0%, #4A5568 50%, #2C3E50 100%)",
                  backgroundSize: "200% auto",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "shimmer 3s linear infinite",
                  "@supports not (background-clip: text)": {
                    background: "none",
                    color: "#2C3E50",
                  },
                  mb: 2,
                  maxWidth: "95%",
                  mx: "auto",
                  "@keyframes shimmer": {
                    "0%": { backgroundPosition: "0% center" },
                    "100%": { backgroundPosition: "200% center" },
                  },
                }}
              >
                {t("hero.title")}
              </Typography>
            </motion.div>

            {/* Subtitle */}
            <motion.div variants={itemVariants}>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: 700,
                  fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: "#64748B",
                }}
              >
                {t("hero.subtitle")}
              </Typography>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ mt: 2 }}
                alignItems="center"
                justifyContent="center"
              >
                <motion.div
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onHoverStart={() => setIsHovered(true)}
                  onHoverEnd={() => setIsHovered(false)}
                >
                  <Button
                    variant="contained"
                    component="a"
                    href="/register"
                    size="large"
                    endIcon={
                      <motion.div
                        animate={{ x: isHovered ? 5 : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <ArrowForwardIcon />
                      </motion.div>
                    }
                    sx={{
                      px: { xs: 3, sm: 5 },
                      py: { xs: 1.2, sm: 2 },
                      fontSize: { xs: "0.9rem", sm: "1.1rem" },
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, #4285F4 0%, #1967D2 100%)",
                      boxShadow: "0 8px 32px rgba(66,133,244,0.35)",
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      whiteSpace: "nowrap",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                        transition: "left 0.5s",
                      },
                      "&:hover": {
                        boxShadow: "0 12px 48px rgba(66,133,244,0.45)",
                        "&::before": { left: "100%" },
                      },
                    }}
                  >
                    {t("hero.cta")}
                  </Button>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outlined"
                    component={ScrollLink}
                    to="contact"
                    smooth
                    duration={600}
                    offset={-100}
                    size="large"
                    sx={{
                      px: { xs: 3, sm: 5 },
                      py: { xs: 1.2, sm: 2 },
                      fontSize: { xs: "0.9rem", sm: "1.1rem" },
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "16px",
                      borderWidth: 2,
                      borderColor: "#4285F4",
                      color: "#4285F4",
                      backgroundColor: "rgba(66,133,244,0.05)",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "#1967D2",
                        backgroundColor: "rgba(66,133,244,0.1)",
                        boxShadow: "0 8px 32px rgba(66,133,244,0.2)",
                      },
                    }}
                  >
                    {t("hero.contact") || "Contact Us"}
                  </Button>
                </motion.div>
              </Stack>
            </motion.div>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}

function TypeAnimationWrapper({ sequence, language, onError }) {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [language]);

  if (hasError) {
    return null;
  }

  try {
    return (
      <TypeAnimation
        key={`${language}-${sequence[0]}`}
        sequence={sequence}
        speed={60}
        repeat={Infinity}
        cursor={true}
        wrapper="span"
        style={{ display: "inline-block" }}
      />
    );
  } catch (error) {
    console.error("TypeAnimation error:", error);
    setHasError(true);
    onError();
    return null;
  }
}

export default HeroSection;
